/**
 * Google Analytics provider implementation.
 *
 * Handles dual GA4 tag support:
 * - Studio GA4 tag (G-R0X7V0YDYW): All events on production
 * - Website GA4 tag (G-0MY276EN9H): Registration and setup routes only
 */

import type { AnalyticsProvider } from '../types/providers.js';
import type { AnalyticsEvent } from '../types/events.js';
import { STUDIO_PRODUCTION_HOST } from './constants.js';

// Define the gtag function type to match the global gtag function
declare global {
  interface Window {
    gtag?: (command: string, action: string, params?: Record<string, any>) => void;
    dataLayer?: any[];
  }
}

export class GoogleAnalyticsProvider implements AnalyticsProvider {
  name = 'google-analytics';
  private initialized = false;
  private websiteTagConfigured = false;

  private readonly STUDIO_GA_TAG = 'G-R0X7V0YDYW';
  private readonly WEBSITE_GA_TAG = 'G-0MY276EN9H';

  /**
   * Check if we're running on production.
   */
  private isProductionHost(): boolean {
    return window.location.host === STUDIO_PRODUCTION_HOST;
  }

  /**
   * Check if current route should send to Website GA4 tag.
   */
  private isRegistrationOrSetupRoute(): boolean {
    const path = window.location.pathname;
    return path === '/register' || path.startsWith('/agent/setup');
  }

  /**
   * Configure the Website GA4 tag.
   * The gtag library is already loaded, we just need to configure the additional tag.
   */
  private configureWebsiteTag(): void {
    if (this.websiteTagConfigured || !window.gtag) return;

    try {
      window.gtag('config', this.WEBSITE_GA_TAG);
      this.websiteTagConfigured = true;
    } catch (error) {
      console.warn('Failed to configure Website GA4 tag:', error);
    }
  }

  /**
   * Initialize the Google Analytics provider.
   */
  async initialize(): Promise<void> {
    try {
      // Only initialize on production host
      if (!this.isProductionHost()) {
        this.initialized = false;
        return;
      }

      // Check if gtag is available (loaded by index.html)
      if (typeof window.gtag !== 'function') {
        console.warn('Google Analytics: gtag not available');
        this.initialized = false;
        return;
      }

      // Configure the Studio GA4 tag
      window.gtag('config', this.STUDIO_GA_TAG);

      // Pre-configure Website tag if on registration/setup routes
      if (this.isRegistrationOrSetupRoute()) {
        this.configureWebsiteTag();
      }

      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
      this.initialized = false;
    }
  }

  /**
   * Track an event with Google Analytics.
   *
   * @param event The event to track
   */
  async track(event: AnalyticsEvent): Promise<void> {
    if (!this.isInitialized()) {
      await this.initialize();
    }

    if (!this.initialized || !window.gtag) {
      return;
    }

    const eventData = {
      event_category: event.category,
      ...event.properties,
      timestamp: event.timestamp || Date.now()
    };

    // Always send to Studio GA4 tag
    window.gtag('event', event.name, {
      ...eventData,
      send_to: this.STUDIO_GA_TAG
    });

    // Also send to Website GA4 tag on registration/setup routes
    if (this.isRegistrationOrSetupRoute()) {
      // Ensure Website tag is configured before sending
      if (!this.websiteTagConfigured) {
        this.configureWebsiteTag();
      }

      window.gtag('event', event.name, {
        ...eventData,
        send_to: this.WEBSITE_GA_TAG
      });
    }
  }

  /**
   * Identify a user with Google Analytics.
   *
   * @param userId The user's ID
   * @param traits Optional user traits/properties
   */
  async identify(userId: string, traits?: Record<string, any>): Promise<void> {
    if (!this.isInitialized()) {
      await this.initialize();
    }

    if (!this.initialized || !window.gtag) {
      return;
    }

    // @ts-expect-error - gtag set command has different signature
    window.gtag('set', {
      user_id: userId,
      ...traits
    });
  }

  /**
   * Reset the Google Analytics state.
   */
  async reset(): Promise<void> {
    if (!this.isInitialized() || !window.gtag) {
      return;
    }

    // GA doesn't have a direct reset method, but we can clear user data
    // @ts-expect-error - gtag set command has different signature
    window.gtag('set', { user_id: undefined });
  }

  /**
   * Check if Google Analytics is initialized.
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}
